import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Hall } from '../firestore-models/hall';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiHall, ApiSetHallsRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { HallDialogComponent } from './hall-dialog/hall-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-hall',
  templateUrl: './hall.component.html',
  styleUrls: ['./hall.component.css'],
  standalone: false,
})
export class HallComponent implements OnInit {

  //Hallコレクション
  private hallsCollection!: AngularFirestoreCollection<Hall>;
  hallsObservable!: Observable<Hall[]>;
  hallsSubscribe!: Subscription;
  //建物情報に紐付くプロジェクトID
  projectId: string;
  //建物情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //建物情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'hall_id', 'name', 'explain', 'menu_disp_type', 'date'];
  dataSource = new MatTableDataSource<Hall>();
  selection = new SelectionModel<Hall>(true, []);

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.hallsCollection = this.angularFirestore.collection<Hall>(
      AppConst.FIRESTORE_HALLS, ref => ref.where('project_id', '==', this.projectId).orderBy('hall_id', 'asc'));
    this.hallsObservable = this.hallsCollection.valueChanges({ idField: 'id' });
    this.hallsSubscribe = this.hallsObservable.subscribe(halls => {
      this.dataSource.data = this.addFilterValue(halls);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.hallsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Hall): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(hall: Hall): void {
    const startDate: any = hall!.sales_start_date ? new Date(hall!.sales_start_date.seconds * 1000) : null;
    const endDate: any = hall!.sales_end_date ? new Date(hall!.sales_end_date.seconds * 1000) : null;
    let apiHall: ApiHall = {
      hall_id: hall.hall_id,
      hall_explain: hall.hall_explain,
      hall_names: hall.hall_names,
      latitude: hall.hall_geopoint ? hall.hall_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
      longitude: hall.hall_geopoint ? hall.hall_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
      show_marker: hall.show_marker,
      project_id: hall.project_id,
      menu_disp_type: hall.menu_disp_type,
      menu_disp_map_range: hall.menu_disp_map_range,
      show_paid_content: hall.show_paid_content,
      hall_descs: hall.hall_descs,
      sales_start_date: startDate,
      sales_end_date: endDate,
      price: hall.price,
      validity_period: hall.validity_period,
      use_custom_menu: hall.use_custom_menu,
      use_custom_menu_icon: hall.use_custom_menu_icon,
      use_custom_side_icon: hall.use_custom_side_icon,
      use_custom_side_image:hall.use_custom_side_image,
      custom_menu_icon_file: hall.custom_menu_icon_file,
      custom_side_icon_file: hall.custom_side_icon_file,
      custom_side_image_file:hall.custom_side_image_file,
      menu_icon: hall.menu_icon,
      side_icon: hall.side_icon,
      enabled: hall.enabled,
    };
    const dialogRef = this.dialog.open(HallDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, hall: apiHall }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(HallDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, hall: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedHalls: Array<ApiHall> = new Array<ApiHall>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const startDate: any = row!.sales_start_date ? new Date(row!.sales_start_date.seconds * 1000) : null;
          const endDate: any = row!.sales_end_date ? new Date(row!.sales_end_date.seconds * 1000) : null;
          const selectedHall: ApiHall = {
            hall_id: row.hall_id,
            hall_explain: row.hall_explain,
            hall_names: row.hall_names,
            latitude: row.hall_geopoint.latitude,
            longitude: row.hall_geopoint.longitude,
            show_marker: row.show_marker,
            project_id: row.project_id,
            menu_disp_type: row.menu_disp_type,
            menu_disp_map_range: row.menu_disp_map_range,
            show_paid_content: row.show_paid_content,
            hall_descs: row.hall_descs,
            sales_start_date: startDate,
            sales_end_date: endDate,
            price: row.price,
            validity_period: row.validity_period,
            use_custom_menu: row.use_custom_menu,
            use_custom_menu_icon: row.use_custom_menu_icon,
            use_custom_side_icon: row.use_custom_side_icon,
            use_custom_side_image: row.use_custom_side_image,
            custom_menu_icon_file: row.custom_menu_icon_file,
            custom_side_icon_file: row.custom_side_icon_file,
            custom_side_image_file: row.custom_side_image_file,
            menu_icon: row.menu_icon,
            side_icon: row.side_icon,
            enabled: row.enabled,
          };
          selectedHalls.push(selectedHall);
        }
      }
      if (!!result) {
        let request: ApiSetHallsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          halls: selectedHalls
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setHalls(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              if (response!.ret === AppConst.API_RET_IN_USE_DATA) {
                this.toastr.error(this.translate.instant('削除できませんでした。フロア・表示グループ情報で利用中のデータが含まれている可能性があります。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'hall_id':
          return compare(a.hall_id, b.hall_id, isAsc);
        case 'name':
          const aName = a.hall_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.hall_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        case 'explain':
          return compare(a.hall_explain, b.hall_explain, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.hall_names) {
        filterValue += value.name + ' '
      }
      filterValue += doc.hall_explain + ' ';
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
